import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { useLazyQuery, useMutation, useQuery, useApolloClient } from '@apollo/client';
import { generatePath, useHistory, useLocation, useParams } from 'react-router-dom';
import {
  FormCard,
  YupNumeric,
  YupDate,
  YupString,
  YupBoolean,
  promiseModalDialog,
  UserTooltip,
  isNumeric,
  toastSuccess,
  FormCol,
} from 'motion-components';
import * as Yup from 'yup';
import * as Lo from 'lodash';
import { gqlGetByIdQuery, gqlAdd, gqlUpdate, gqlDelete, FormInput } from '../graphql';
import '../../../../styles/tab-styles.css';
import { FormFields } from './form-fields';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import { useTranslation } from 'react-i18next';
import { LegacyTooltip } from 'shared-pages/components/legacy-tooltip';
import { ButtonCloseModal, RowFieldTooltips } from 'shared-pages/components';
import { Button } from 'react-bootstrap';
import { FaArrowAltCircleLeft, FaSave } from 'react-icons/fa';
import { Company, CurrentAppCompany, requiredString } from 'shared-pages/common/constants';
import { getRouteByName } from 'app/routes';

type Props = {
  isModal?: boolean;
  paramId?: string | undefined;
  paramCollectorId?: number | undefined;
  modalOnClose?: (id: number) => void | undefined;
};

export const SeriesFormPage = ({
  isModal = false,
  paramId = undefined,
  paramCollectorId,
  modalOnClose,
}: Props) => {
  const { handleGraphqlError } = useHandleError('driver');
  const { appStore } = useRootStore();
  const history = useHistory();
  const location: any = useLocation();
  const { id: urlId } = useParams<{ id: string }>();
  const id = isModal ? paramId : urlId;
  const idNum = isNumeric(id) ? parseInt(id) : -1;
  const isNew = idNum < 0;
  const { t } = useTranslation();

  const formShape = {
    id: YupNumeric,
    docPrefix: YupString,
    name: YupString,
    collectorId: YupNumeric.required(requiredString),
    edocSeriesNumbers: Yup.array().of(
      Yup.object().shape({
        id: YupNumeric,
        year: YupNumeric,
        lastNumber: YupNumeric,
        lastCode: YupString,
      }),
    ),
    edocSeriesType: Yup.object().shape({
      id: YupNumeric,
      value: YupString,
      name: YupString,
    }),
    startNumber: YupNumeric.required(requiredString),
    endNumber: YupNumeric.required(requiredString),
    isYearlyRenumbering: YupBoolean,
    isActive: YupBoolean,
    firstDate: YupDate,
    lastDate: YupDate,
    firstUser: YupString,
    lastUser: YupString,
    seriestypeId: YupNumeric.required(requiredString),
  };

  const validationSchema = Yup.object().shape(formShape);
  const pickProperties = (values) => Lo.pick(values, Lo.keys(formShape));
  type FormValuesType = Yup.InferType<typeof validationSchema>;

  const [addNewRecord] = useMutation(gqlAdd);
  const [updateRecord] = useMutation(gqlUpdate);
  const [deleteRecord] = useMutation(gqlDelete);
  const [get, { data: rawData, error, refetch }] = useLazyQuery(gqlGetByIdQuery);
  const data = rawData ? rawData[Object.keys(rawData)[0]] : undefined;
  const [initialNewRecData, setInitialNewRecData] = useState<any>();
  const lastNumber = data?.edocSeriesNumbers[0]?.lastNumber;
  /** Fetch data or Initialize new record */
  useEffect(() => {
    if (isNew) {
      // initialize new record
      initializeNewRecord();
    } else {
      // fetch data
      get({ variables: { id: idNum } });
    }
  }, [get, id, idNum]);

  /** Αρχικοποίησε Νέα Εγγραφή  */
  const initializeNewRecord = async () => {
    let newRec: any = { isActive: true };
    console.log('initializeNewRecord', appStore.isCollector, paramCollectorId);
    if (appStore.isCollector) {
      newRec = {
        ...newRec,

        collectorId: appStore.associatedId,
      };
    } else if (paramCollectorId) {
      newRec = {
        ...newRec,
        collectorId: paramCollectorId,
      };
    }
    setInitialNewRecData(newRec);
  };

  // Handler να πάει στην πίσω σελίδα
  const handleReturn = useCallback(() => {
    history.push(generatePath(getRouteByName('Series')), { ...location.state });
  }, [history, location.state]);

  /**
   * * ΑΠΟΘΗΚΕΥΣΗ - UPDATE/CREATE
   */
  const handleSubmit = async (values: FormValuesType, actions) => {
    actions.setSubmitting(false);
    console.log('values:Submit', values);
    const mutValues: any = Lo.pick(values, Lo.keys(new FormInput()));
    //parseInt startNumber endNumber
    mutValues.startNumber = parseInt(mutValues.startNumber);
    mutValues.endNumber = parseInt(mutValues.endNumber);

    // Αν είναι νέα εγγραφή τοτε γράφω όλα τα πεδία και κάνω validate οτι το πρόθεμα με startNumber&endNumber δεν υπάρχει,
    //μπορει να υπάρχει πρόθεμα ίδιο αλλά με υποακολουθίες  αριθμών
    if (isNew) {
      console.log('[IDoc] Add:', mutValues);
      const rec = await addNewRecord({ variables: { data: { ...mutValues, id: idNum } } })
        .then((d) => {
          const rec = d.data[Object.keys(d.data)[0]];
          toastSuccess(`Επιτυχημένη Προσθήκη Εγγραφής [${rec?.id}]`);
          return rec;
        })
        .catch((error) => {
          handleGraphqlError('mutation-add', error);
          return null;
        });
      if (rec) {
        if (isModal) {
          if (modalOnClose) modalOnClose(rec.id);
        }
      }
    } else {
      // Αν είναι update εγγραφής τοτε γράφω μονο τα αλλαγμένα πεδία
      console.log('[Applog] Update:', mutValues);
      updateRecord({ variables: { data: { ...mutValues, id: idNum } } })
        .then((d) => {
          toastSuccess(`Επιτυχής Ενημέρωση Εγγραφής [${d.data[Object.keys(d.data)[0]].id}]`);
          refetch();
        })
        .catch((error) => {
          handleGraphqlError('mutation-update', error);
        });
    }
  };

  /**
   * *  DELETE
   */
  const handleDelete = useCallback(async () => {
    const res = await promiseModalDialog({
      title: 'Διαγραφή Εγγραφής',
      text: 'Είστε σίγουροι ότι θέλετε να διαγράψετε την Εγγραφή;',
    });
    if (res) {
      deleteRecord({ variables: { id: idNum } })
        .then(() => history.goBack())
        .catch((error) => {
          handleGraphqlError('mutation-delete', error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteRecord, idNum]);


  const permitDelete = useMemo(() => {
    // A series can be deleted if it has not been used yet (lastNumber is null/undefined)
    const lastNumber = data?.edocSeriesNumbers?.[0]?.lastNumber;
    const isUnused = !lastNumber || isNew;

    // Define allowed User roles by company.
    // If a company is not listed, it falls back to the default permission rule.
    const companyRules: Record<string, boolean> = {
      [Company.Rebattery]: appStore.isPlatformOwner,
      [Company.RebatteryLiion]: appStore.isPlatformOwner,
      [Company.Fotokiklosi]: appStore.isPlatformOwner,
      // You can easily add other companies here:
      // [Company.AfisCyprus]: appStore.isPlatformOwner || appStore.isCollector,
    };

    const isUserAllowed = CurrentAppCompany in companyRules
      ? companyRules[CurrentAppCompany]
      : (appStore.isPlatformOwner || appStore.isCollector); // Default fallback

    return isUnused && isUserAllowed;
  }, [data, isNew, appStore.isPlatformOwner, appStore.isCollector]);

  if (error) {
    handleGraphqlError('query-get', error);
    return <h3 className="text-center">{error.message}</h3>;
  } else {
    return (
      <FormCard
        title={
          isNew
            ? `${t('Δήλωση Νέας Σειράς Αρίθμησης')}`
            : `${t('Σειρά Αρίθμησης')}: ${data?.docPrefix ?? '-'}`
        }
        onSubmit={handleSubmit}
        onDelete={handleDelete}
        onReturn={!isModal ? handleReturn : null}
        permitSubmit={false}
        permitDelete={permitDelete}
        initialValues={isNew ? initialNewRecData : pickProperties(data)}
        validationSchema={validationSchema}
        enableReinitialize
        idx={idNum}
        injectFooterElements={
          !isModal
            ? undefined
            : (formikCtx) => {
                return (
                  <ButtonCloseModal
                    onClick={() => {
                      modalOnClose(-1);
                    }}
                  />
                );
              }
        }
      >
        <FormFields isNew={isNew} updateRecord={updateRecord} refetch={refetch} data={data} />
        {appStore.isPlatformOwner && <RowFieldTooltips data={data} />}
      </FormCard>
    );
  }
};
